import React, { useState } from "react";
import { useAppDispatch } from "../../../../store";
import { addSmallCraft } from "../../../../store/features/shipBuilderSlice";
import { SmallCraft } from "../../../../types/ship";

interface SmallCraftAssignmentModalProps {
  systemId: string;
  systemName: string;
  systemType: 'external' | 'internal';
  capacity: number | 'unlimited';
  configuredSystem: any; // The configured system to add
  onClose: () => void;
  onCraftAssigned?: (craft: SmallCraft) => void;
  onAddSystem?: (system: any) => void; // Function to add the system
}

/**
 * Modal component for assigning small craft to a docking system
 * Used when adding docking systems in the Optional Systems step
 */
export const SmallCraftAssignmentModal: React.FC<SmallCraftAssignmentModalProps> = ({
  systemId,
  systemName,
  systemType,
  capacity,
  configuredSystem,
  onClose,
  onCraftAssigned,
  onAddSystem,
}) => {
  const dispatch = useAppDispatch();
  const [newCraft, setNewCraft] = useState<Partial<SmallCraft>>({
    id: '',
    type: '',
    size: 0,
    quantity: 1,
    dockingLocation: systemType,
    assignedSystem: systemId,
  });

  const canAddCraft = Boolean(newCraft.type && newCraft.size && newCraft.size > 0 && newCraft.quantity && newCraft.quantity > 0);

  const handleAddCraft = () => {
    if (!canAddCraft) return;

    // First add the system
    if (onAddSystem && configuredSystem) {
      onAddSystem(configuredSystem);
    }

    const craftToAdd: SmallCraft = {
      id: `craft-${Date.now()}`,
      type: newCraft.type!,
      size: newCraft.size!,
      quantity: newCraft.quantity!,
      details: `${newCraft.type} (${newCraft.size} tons, quantity: ${newCraft.quantity})`,
      dockingLocation: systemType,
      assignedSystem: systemId,
    };

    dispatch(addSmallCraft(craftToAdd));
    
    if (onCraftAssigned) {
      onCraftAssigned(craftToAdd);
    }

    // Close modal
    onClose();
  };

  const handleClose = () => {
    setNewCraft({
      id: '',
      type: '',
      size: 0,
      quantity: 1,
      dockingLocation: systemType,
      assignedSystem: systemId,
    });
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Assign Small Craft to {systemName}
          </h3>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            ✕
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Craft Type *
            </label>
            <input
              type="text"
              value={newCraft.type || ''}
              onChange={(e) => setNewCraft({ ...newCraft, type: e.target.value })}
              placeholder="e.g., Shuttle, Fighter, Launch"
              className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Size (tons) *
            </label>
            <input
              type="number"
              min="1"
              value={newCraft.size || ''}
              onChange={(e) => setNewCraft({ ...newCraft, size: Math.max(1, parseInt(e.target.value) || 0) })}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Quantity *
            </label>
            <input
              type="number"
              min="1"
              value={newCraft.quantity || ''}
              onChange={(e) => setNewCraft({ ...newCraft, quantity: Math.max(1, parseInt(e.target.value) || 0) })}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            />
          </div>

          {capacity !== 'unlimited' && (
            <div className="text-sm text-gray-600 dark:text-gray-400">
              <strong>Capacity:</strong> {capacity} tons
              {newCraft.size && newCraft.quantity && (
                <span className="ml-2">
                  ({newCraft.size * newCraft.quantity} tons used)
                </span>
              )}
            </div>
          )}

          {capacity !== 'unlimited' && Boolean(newCraft.size && newCraft.quantity) && (newCraft.size! * newCraft.quantity!) > Number(capacity) && (
            <div className="text-sm text-red-600 dark:text-red-400">
              ⚠️ Craft exceeds system capacity
            </div>
          )}
        </div>

        <div className="flex space-x-3 mt-6">
          <button
            onClick={handleClose}
            className="flex-1 bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-300 px-4 py-2 rounded-md hover:bg-gray-400 dark:hover:bg-gray-500"
          >
            Cancel
          </button>
          <button
            onClick={() => {
              if (onAddSystem && configuredSystem) {
                onAddSystem(configuredSystem);
              }
              onClose();
            }}
            className="flex-1 bg-yellow-600 text-white px-4 py-2 rounded-md hover:bg-yellow-700"
          >
            Skip & Add System
          </button>
          <button
            onClick={handleAddCraft}
            disabled={!canAddCraft || (capacity !== 'unlimited' && Boolean(newCraft.size && newCraft.quantity) && (newCraft.size! * newCraft.quantity!) > Number(capacity))}
            className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            Add Craft
          </button>
        </div>

        <div className="mt-4 text-xs text-gray-500 dark:text-gray-400">
          <p>You can assign additional small craft later in the Cargo Configuration step.</p>
        </div>
      </div>
    </div>
  );
};
